import { useEffect, useId, useRef } from "react";
import "./Pop-up-container.css";

// Module-level ref count: the notifications bell can open a related-operation
// modal while a page's own popup is already mounted, so two of these can
// legitimately be open at once. Without counting, the second to unmount would
// reset body.overflow while the first is still open, silently unlocking
// background scroll.
let openPopupCount = 0;

// Which popup Escape belongs to. Each open popup pushes a token on mount and
// takes it out on unmount; only the one on top answers Escape. Before this,
// every open popup closed on the same key press, so Escape in the bell's
// related-operation modal also closed the page's popup underneath it.
const stack = [];

/**
 * Whether any popup currently holds the scroll lock.
 *
 * Exported for the UNRELATED WRITERS problem, which is separate from the
 * nesting one above: AppLayout writes the same body.overflow property for the
 * mobile drawer, and clearing it blindly on drawer close unlocked the page
 * underneath a still-open modal. The seller app hit this first and fixed it the
 * same way — see layout/src/components/sideBarOverly/SideBarOverly.js.
 */
export function popupsAreOpen() {
  return openPopupCount > 0;
}

// A SweetAlert2 confirm opened over a popup owns Escape: it listens on its own
// dialog, which sits below document, so stopping the key here would keep it
// from ever arriving there, and the popup underneath would close instead.
function sweetAlertIsOpen() {
  const body = document.body.classList;
  return body.contains("swal2-shown") && !body.contains("swal2-toast-shown");
}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * busy: something the popup started is still on its way (a save, a refund).
 * While it is, Escape, the X and the backdrop leave the popup open, so the
 * admin sees how it ended instead of losing the answer with the popup.
 *
 * Layout (2026-09-30, the seller app's record popup rules): the header stays
 * put, only .pop-up-body scrolls, and a button row marked .pop-up-footer sticks
 * to the bottom of it. Focus moves inside on open, Tab stays inside, and focus
 * goes back to whatever opened it on close.
 */
function PoPUpContainer({ popUpTitle, title, titleIcon, hint, size, cancel, busy = false, children }) {
  const titleId = useId();
  const panelRef = useRef(null);
  const cancelRef = useRef(cancel);
  const busyRef = useRef(busy);
  cancelRef.current = cancel;
  busyRef.current = busy;

  useEffect(() => {
    openPopupCount += 1;
    document.body.style.overflow = "hidden";
    return () => {
      openPopupCount = Math.max(0, openPopupCount - 1);
      if (openPopupCount === 0) {
        document.body.style.overflow = "";
      }
    };
  }, []);

  useEffect(() => {
    const token = {};
    stack.push(token);
    // Focus inside: the first field if the popup is a form, else the panel.
    // Not the field on a touch screen, where it would throw the keyboard up
    // over the sheet before the admin has read it.
    const opener = document.activeElement;
    const panel = panelRef.current;
    const touch = typeof window.matchMedia === "function" && window.matchMedia("(hover: none)").matches;
    const field = touch ? null : panel?.querySelector("textarea, input:not([type=hidden]), select");
    (field || panel)?.focus({ preventScroll: true });
    // Capture phase, so the top popup sees Escape before anything inside it or
    // any other document listener, and can stop it there.
    const onKeyDown = (e) => {
      if (stack[stack.length - 1] !== token) return;
      if (e.key === "Tab" && panel && !sweetAlertIsOpen()) {
        const all = [...panel.querySelectorAll(FOCUSABLE)];
        const shown = all.filter((el) => el.offsetParent !== null);
        // Nothing measured as shown only where there is no layout (tests).
        const items = shown.length ? shown : all;
        if (!items.length) { e.preventDefault(); return; }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        return;
      }
      if (e.key !== "Escape") return;
      if (sweetAlertIsOpen()) return;
      if (busyRef.current) return;
      e.stopPropagation();
      cancelRef.current?.();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      const at = stack.indexOf(token);
      if (at !== -1) stack.splice(at, 1);
      if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  const close = () => {
    if (busyRef.current) return;
    cancelRef.current?.();
  };

  return (
    <div className="premium-modal-overlay">
      {/* Separate element rather than a click handler on the overlay: the
          panel is a child of the overlay, so a click inside the panel would
          otherwise bubble up and close the modal. */}
      <div className="pop-up-backdrop" onClick={close} data-testid="pop-up-backdrop" />

      <div
        ref={panelRef}
        className={`premium-modal-content ${size || ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        tabIndex={-1}
      >
        <div className="modal-header">
          <div className="modal-header-left">
            <span className="modal-title-icon">
              <i className={titleIcon || "fas fa-layer-group"}></i>
            </span>
            <div>
              <h3 id={titleId}>{popUpTitle || title}</h3>
              {hint && <p className="modal-hint">{hint}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            className="modal-close-btn"
            title={busy ? "Wait until this finishes" : "Close"}
            aria-label="Close"
            disabled={busy}
            aria-disabled={busy || undefined}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="pop-up-body">{children}</div>
      </div>
    </div>
  );
}

export default PoPUpContainer;
